import { useState } from "react";
import { Box, Dialog, DialogContent, DialogTitle, IconButton, Tooltip, Typography } from "@mui/material";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import CloseIcon from "@mui/icons-material/Close";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { ACCENTS, CARD_SX, COLORS, SHADOW_HOVER, TYPE } from "./emsTheme";
import { InfoTip } from "./ChartCard";
import { downloadCSV } from "../../helpers/UtilityHelper";

const MUTED = COLORS.ink3;
const NAVY = COLORS.ink;

const csvCell = (v) => {
    const s = v == null ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

/** columns: [{ key, label, align, text(row) -> export/display text, render(row) -> optional node,
 *  onClick(row) -> optional drill-down }]. Rows are plain objects from the API. */
export const exportRows = (columns, rows, fileName) => {
    const lines = [columns.map((c) => csvCell(c.label)).join(",")];
    rows.forEach((row) => lines.push(columns.map((c) => csvCell(c.text ? c.text(row) : row[c.key])).join(",")));
    downloadCSV(lines.join("\n"), fileName);
};

const Table = ({ columns, rows, maxHeight }) => (
    <Box sx={{ maxHeight, overflow: "auto" }}>
        <Box component="table" sx={{
            width: "100%", borderCollapse: "collapse", fontSize: 13,
            "& th": { color: MUTED, fontWeight: 700, fontSize: 12, textAlign: "right", px: 1.25, py: 0.9, position: "sticky", top: 0, bgcolor: COLORS.surface, zIndex: 1, whiteSpace: "nowrap", borderBottom: `1px solid ${COLORS.grid}` },
            "& td": { textAlign: "right", px: 1.25, py: 0.8, borderBottom: `1px solid ${COLORS.grid}`, fontVariantNumeric: "tabular-nums", color: NAVY, whiteSpace: "nowrap" },
            "& th.left, & td.left": { textAlign: "left" },
            "& tbody tr:hover td": { bgcolor: COLORS.ground },
        }}>
            <thead>
                <tr>{columns.map((c) => <th key={c.key} className={c.align === "left" ? "left" : undefined}>{c.label}</th>)}</tr>
            </thead>
            <tbody>
                {rows.map((row, i) => (
                    <tr key={row.rowKey || row.key || i}>
                        {columns.map((c) => {
                            const content = c.render ? c.render(row) : (c.text ? c.text(row) : row[c.key]);
                            const clickable = c.onClick && row[c.key];
                            return (
                                <td key={c.key} className={c.align === "left" ? "left" : undefined}
                                    style={c.align === "left" ? { maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis", fontWeight: 500 } : undefined}>
                                    {clickable ? (
                                        <Box component="button" type="button" onClick={() => c.onClick(row)}
                                            sx={{ all: "unset", color: COLORS.purple, fontWeight: 600, cursor: "pointer", "&:hover": { textDecoration: "underline" }, "&:focus-visible": { outline: `2px solid ${COLORS.purple}` } }}>
                                            {content}
                                        </Box>
                                    ) : content}
                                </td>
                            );
                        })}
                    </tr>
                ))}
            </tbody>
        </Box>
    </Box>
);

/**
 * A titled table that can be opened full screen and exported as CSV — the analytics pages'
 * stand-in for Freshdesk report widgets (expand + export on every table).
 */
const AnalyticsTableCard = ({ icon, eyebrow, title, sub, info, columns, rows, fileName, headerExtra, emptyText = "No tickets for these filters", maxHeight = 380 }) => {
    const [expanded, setExpanded] = useState(false);
    const list = rows || [];
    const actions = (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {headerExtra}
            <Tooltip title="Export CSV">
                <span>
                    <IconButton size="small" disabled={!list.length} onClick={() => exportRows(columns, list, fileName)} aria-label="Export CSV">
                        <FileDownloadOutlinedIcon fontSize="small" />
                    </IconButton>
                </span>
            </Tooltip>
            <Tooltip title="Expand">
                <IconButton size="small" onClick={() => setExpanded(true)} aria-label="Expand">
                    <OpenInFullIcon sx={{ fontSize: 17 }} />
                </IconButton>
            </Tooltip>
        </Box>
    );
    const body = list.length === 0
        ? <Typography sx={{ fontSize: 13, color: MUTED, py: 2, textAlign: "center" }}>{emptyText}</Typography>
        : null;

    return (
        <>
            <Box sx={{ ...CARD_SX, p: 2.5, display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 1.5, minWidth: 0, "& > *": { minWidth: 0 },
                transition: "box-shadow 200ms", "&:hover": { boxShadow: SHADOW_HOVER } }}>
                <Box sx={{ display: "flex", gap: 1.25, alignItems: "flex-start", flexWrap: "wrap" }}>
                    {icon && (
                        <Box aria-hidden sx={{ width: 32, height: 32, borderRadius: "9px", background: ACCENTS.purple.grad, color: "#fff", display: "grid", placeItems: "center", flexShrink: 0,
                            boxShadow: "0 4px 10px rgba(102,63,149,.25)", "& svg": { fontSize: 18 } }}>{icon}</Box>
                    )}
                    <Box sx={{ flex: "1 1 220px", minWidth: 0 }}>
                        {eyebrow && <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: COLORS.purple, mb: 0.25 }}>{eyebrow}</Typography>}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <Typography component="h3" sx={{ ...TYPE.cardTitle, fontWeight: 700, color: NAVY, textWrap: "balance" }}>{title}</Typography>
                            {info && <InfoTip text={info} />}
                        </Box>
                        {sub && <Typography sx={{ fontSize: 12, color: MUTED, mt: 0.25 }}>{sub}</Typography>}
                    </Box>
                    {actions}
                </Box>
                {body || <Table columns={columns} rows={list} maxHeight={maxHeight} />}
            </Box>
            <Dialog open={expanded} onClose={() => setExpanded(false)} fullScreen>
                <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, borderBottom: "1px solid #E7EAF0" }}>
                    <Box sx={{ flex: 1 }}>
                        <Typography sx={{ fontSize: 16, fontWeight: 800, color: NAVY }}>{title}</Typography>
                        {sub && <Typography sx={{ fontSize: 12, color: MUTED }}>{sub}</Typography>}
                    </Box>
                    {headerExtra}
                    <Tooltip title="Export CSV">
                        <span>
                            <IconButton disabled={!list.length} onClick={() => exportRows(columns, list, fileName)} aria-label="Export CSV">
                                <FileDownloadOutlinedIcon />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <IconButton onClick={() => setExpanded(false)} aria-label="Close"><CloseIcon /></IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: 2 }}>
                    {body || <Table columns={columns} rows={list} maxHeight="calc(100vh - 120px)" />}
                </DialogContent>
            </Dialog>
        </>
    );
};

export default AnalyticsTableCard;
